import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextButton } from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import { usePermissions } from '../../hooks/usePermissions';
import { rbacApi } from '../../lib/rbac';
import { useCompanyRbac } from './roles/useCompanyRbac';
import { LegalGuidanceSection } from './roles/LegalGuidanceSection';
import { AssignmentsSection } from './roles/AssignmentsSection';
import { PermissionMatrixSection } from './roles/PermissionMatrixSection';
import { rbacErrorMessage } from './roles/rbacErrors';

/**
 * 역할·선임 (/settings/roles) — 조직 RBAC의 조직 화면(줄기 company-rbac P2-a, 결정
 * `2026-09-20-company-rbac-with-legal-positions` §6). 진입 게이트는 **권한 key `org.roles.manage`** 다
 * (P7-g, 2026-09-25) — 서버 `company-rbac.controller`가 GET까지 그 키로 가드하므로(권한 매트릭스도 편집
 * 화면의 일부) 소유자 성질로 좁히면 「서버는 허용하는데 화면에 없는」 자리가 된다. 소유자는 서버가 전체
 * 키를 주므로(`resolvePermissions`) 키로 묻는 것이 소유자를 **이미 포함**한다.
 *
 * 섹션 순서(결정 §6): ① 상시근로자 수 → 선임 안내 ② 직위별 배정 ③ 역할·권한 매트릭스.
 * 데이터는 `useCompanyRbac`(권한 정의·역할·배정·개요), 쓰기 뒤 재조회는 축별(배정 ↔ 역할).
 */
export default function RolesSettings() {
  const navigate = useNavigate();
  const { currentMembership, fetchUser } = useAuthStore();
  const { showToast } = useToastStore();
  const companyId = currentMembership?.companyId;
  // 행위 판정은 `can(key)`(company-rbac P4 ① 규약) — 서버 가드와 같은 키다.
  const { can } = usePermissions();
  const canManageRoles = can('org.roles.manage');
  const deviceLayout = useDeviceLayout();
  const isMobile = deviceLayout === 'phone' || deviceLayout === 'fold';

  const rbac = useCompanyRbac(companyId);
  const [isSavingOverview, setIsSavingOverview] = useState(false);

  useEffect(() => {
    if (!canManageRoles) navigate('/');
  }, [canManageRoles, navigate]);

  const handleSaveOverview = async (regularWorkerCount: number | null) => {
    if (!companyId) return;
    setIsSavingOverview(true);
    try {
      const next = await rbacApi.updateOverview(companyId, { regularWorkerCount });
      rbac.setOverview(next);
      showToast('상시근로자 수를 저장했습니다.', 'success');
    } catch (err) {
      showToast(rbacErrorMessage(err, '저장에 실패했습니다.'), 'error');
    } finally {
      setIsSavingOverview(false);
    }
  };

  // 배정·역할 권한이 바뀌면 내 멤버십의 `permissions`도 바뀔 수 있다 — `/auth/me`를 다시 읽어 `can(key)`를 맞춘다.
  const handleAssignmentsChanged = async () => {
    await rbac.reloadAssignments();
    await fetchUser();
  };
  const handleRolesChanged = async () => {
    await rbac.reloadRoles();
    await fetchUser();
  };

  if (!companyId) {
    return (
      <div className="text-center py-12">
        <p className="text-ink-500">조직을 선택해주세요.</p>
      </div>
    );
  }

  return (
    <div className={isMobile ? 'space-y-4' : 'space-y-6'}>
      {isMobile ? (
        <h1 className="text-lg font-bold tracking-tight text-ink-900">역할·선임</h1>
      ) : (
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">역할·선임</h1>
          <p className="mt-1 text-[15px] font-medium text-ink-500">
            법정 선임 직위와 조직 역할을 배정하고, 역할별 권한을 조정합니다.
          </p>
        </div>
      )}

      {rbac.error && (
        <div className="rounded-card bg-rose-50 p-4">
          <p className="text-sm text-rose-700">{rbac.error}</p>
          <TextButton tone="danger" onClick={() => rbac.reloadAll()} className="mt-2 text-sm">
            다시 시도
          </TextButton>
        </div>
      )}

      {rbac.isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : (
        <>
          <LegalGuidanceSection
            overview={rbac.overview}
            isMobile={isMobile}
            isSaving={isSavingOverview}
            onSave={handleSaveOverview}
          />
          <AssignmentsSection
            companyId={companyId}
            roles={rbac.roles}
            assignments={rbac.assignments}
            isMobile={isMobile}
            onChanged={handleAssignmentsChanged}
          />
          <PermissionMatrixSection
            companyId={companyId}
            permissions={rbac.permissions}
            roles={rbac.roles}
            onChanged={handleRolesChanged}
          />
        </>
      )}
    </div>
  );
}
